// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EducationalTooltip } from '@/components/educational-tooltip';
import { HorizontalNavigationLink } from '@/components/horizontal-navigation-link';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { ReactNode, useRef } from 'react';

interface Props {
  /**
   * The primary action for the active catalog section (e.g. an add button),
   * rendered at the end of the title row.
   */
  action?: ReactNode;
}

/**
 * The shared header for catalog listing pages: a "Catalog" title with an
 * educational tooltip and the active section's action, above the
 * Items/Bundles section tabs.
 */
export function CatalogPageHeader(props: Props) {
  const infoTooltip = useDisclosure();
  const infoButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">Catalog</Typography>
          <IconButton
            ref={infoButtonRef}
            size="small"
            onClick={infoTooltip.open}
            aria-label="Information about the catalog"
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Stack>
        <EducationalTooltip
          isOpen={infoTooltip.isOpen}
          onClose={infoTooltip.close}
          anchorElement={infoButtonRef.current}
          title="Catalog"
          placement="bottom-start"
        >
          <Stack spacing={2}>
            <Stack spacing={1}>
              <Typography variant="subtitle1">Items</Typography>
              The catalog contains all items that can be tracked in Hardfin.
              Items are classified into three types:
              <Box component="ul" sx={{ pl: 2, mt: 1, mb: 1 }}>
                <li>
                  <strong>Service</strong> - Non-physical items like software,
                  subscriptions, or services
                </li>
                <li>
                  <strong>Device</strong> - Physical items tracked individually
                  by serial number
                </li>
                <li>
                  <strong>Bulk</strong> - Components tracked by quantity, not
                  individual units
                </li>
              </Box>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle1">Bundles</Typography>
              Bundles are sets of catalog items you frequently add together,
              each with a default quantity. Applying a bundle to a document,
              such as a Fulfillment Order, adds the items from the bundle as
              individual line items on the document.
            </Stack>
          </Stack>
        </EducationalTooltip>
        {props.action}
      </Stack>
      <Stack direction="row" sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <HorizontalNavigationLink to="/catalog/items">
          Items
        </HorizontalNavigationLink>
        <HorizontalNavigationLink to="/catalog/bundles">
          Bundles
        </HorizontalNavigationLink>
      </Stack>
    </Stack>
  );
}
